/**
 * Utility functions for generating and managing thumbnails
 */

export interface ThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
}

/**
 * Generate a thumbnail from an image URL using canvas
 * This is a client-side solution for simple thumbnail generation
 */
export const generateThumbnail = (
  imageUrl: string, 
  options: ThumbnailOptions = {}
): Promise<string> => {
  const { width = 160, height = 160, quality = 0.8 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Calculate dimensions to maintain aspect ratio
      const aspectRatio = img.width / img.height;
      let drawWidth = width;
      let drawHeight = height;
      let offsetX = 0;
      let offsetY = 0;

      if (aspectRatio > 1) {
        // Landscape
        drawHeight = width / aspectRatio;
        offsetY = (height - drawHeight) / 2;
      } else {
        // Portrait or square
        drawWidth = height * aspectRatio;
        offsetX = (width - drawWidth) / 2;
      }

      // Draw image
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      // Convert to data URL
      const thumbnailUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(thumbnailUrl);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
};

/**
 * Simple thumbnail URL generator for static images
 * For production, you might want to use a service like Cloudinary or similar
 */
export const createThumbnailUrl = (originalUrl: string, size: number = 160): string => {
  // If using a CDN service, you could modify the URL here
  // For now, we'll return the original URL and rely on CSS sizing
  // In production, you might do something like:
  // return `${originalUrl}?w=${size}&h=${size}&fit=crop&fm=webp`;
  
  return originalUrl;
};

/**
 * Preload images for better performance
 */
export const preloadImages = (urls: string[]): Promise<void[]> => {
  const promises = urls.map(url => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load ${url}`));
      img.src = url;
    });
  });

  return Promise.all(promises);
};

/**
 * Create optimized image data for the ImageSelector component
 */
export const createImageData = (
  images: Array<{
    id: string;
    src: string;
    label: string;
    alt?: string;
  }>,
  thumbnailSize: number = 160
) => {
  return images.map(img => ({
    ...img,
    thumbnail: createThumbnailUrl(img.src, thumbnailSize)
  }));
}; 